import {
  facilityTypeLabels,
  getExportFacilitiesUrl,
  getExportGoodsAndServicesUrl,
  getExportLogisticsPackagingUrl,
  getExportUtilitiesUrl,
  IFacility,
  UtilityType,
} from "@/api/facilities";
import ActionsPopover from "@/components/ActionsPopOver/ActionsPopOver";
import ActionsPopOverItem from "@/components/ActionsPopOver/ActionsPopOverItem";
import FakeLink from "@/components/FakeLink/FakeLink";
import FileDownloader from "@/components/FileDownloader/FileDownloader";
import HelpButton from "@/components/HelpButton/HelpButton";
import PageButtonGroup from "@/components/PageButtonGroup/PageButtonGroup";
import { PageHeader } from "@/components/PageHeader/PageHeader";
import StatusTag from "@/components/StatusTag/StatusTag";
import StatusTags from "@/components/StatusTags/StatusTags";
import UnrelatedTag from "@/components/StatusTags/UnrelatedTag";
import SwitchWithLabel from "@/components/SwitchWithLabel/SwitchWithLabel";
import Table, { Column } from "@/components/Table/Table";
import useTableStateUrl from "@/components/Table/hooks/useTableStateUrl";
import TableHeader from "@/components/TableHeader/TableHeader";
import YearSelector from "@/components/YearSelector/YearSelector";
import CapitalGoodsIcon from "@/components/icons/CapitalGoodsIcon";
import CompleteIcon from "@/components/icons/CompleteIcon";
import FacilitiesIcon from "@/components/icons/chapters/FacilitiesIcon";
import IncompleteIcon from "@/components/icons/chapters/IncompleteIcon";
import PackageIcon from "@/components/icons/chapters/PackageIcon";
import useAnalytics from "@/hooks/useAnalytics";
import { useGetFacilities, useGetFacilityOverviewYears } from "@/hooks/useFacilities";
import useTranslation from "@/hooks/useTranslation";
import useUrlStateBoolean from "@/hooks/useUrlStateBoolean";
import { useDashboardYear } from "@/pages/dashboard/hooks/useDateRange";
import BulkEditScopeModal from "@/pages/facilities/BulkEditScopeModal";
import DownloadLogisticsPackagingTemplate from "@/pages/facilities/DownloadLogisticsPackagingTemplate";
import UploadLogisticsPackaging from "@/pages/facilities/UploadLogisticsPackaging";
import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  GlobalOutlined,
  MoreOutlined,
  PlusOutlined,
  ThunderboltOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { Button, Dropdown, DropdownProps, Space, Tooltip } from "antd";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import DeleteFacilityAction from "./DeleteFacilityAction";
import DownloadFacilitiesTemplate from "./DownloadFacilitiesTemplate";
import DownloadGoodsAndServicesTemplate from "./DownloadGoodsAndServicesTemplate";
import DownloadUtilitiesTemplate from "./DownloadUtilitiesTemplate";
import styles from "./FacilityOverview.module.scss";
import UploadFacilitiesButton from "./UploadFacilitiesButton";
import UploadGoodsAndServices from "./UploadGoodsAndServices";
import UploadUtilitiesButton from "./UploadUtilitiesButton";
import useFacilityId from "./hooks/useFacilityId";

export default function FacilitiesOverview() {
  const facilityId = useFacilityId();
  const navigate = useNavigate();
  const [selectedFacilities, setSelectedFacilities] = useState<IFacility[]>([]);
  const analytics = useAnalytics();
  const translation = useTranslation();
  const [showUnrelated, setShowUnrelated] = useUrlStateBoolean("showUnrelated", false);

  const [moreDropdownOpen, setMoreDropdownOpen] = React.useState(false);

  const handleOpenChange: DropdownProps["onOpenChange"] = (nextOpen, info) => {
    if (info.source === "trigger" || nextOpen) {
      setMoreDropdownOpen(nextOpen);
    }
  };

  const { year: selectedYear } = useDashboardYear();

  const deselectUndeletableFacilities = () => {
    setSelectedFacilities((selectedFacilities) => [
      ...selectedFacilities.filter((x) => x.deletable),
    ]);
  };

  const onSelectedKeysChange = (keys: number[]) => {
    analytics.track("Product Selected");
    setSelectedFacilities((selectedFacilities) => {
      let productsOnOtherPages = [
        ...selectedFacilities.filter((x) => !data?.data.map((x) => x.id).includes(x.id)),
      ];
      return [
        ...productsOnOtherPages,
        ...(data?.data.filter((product) => keys.includes(product.id)) || []),
      ];
    });
  };

  const notUsedRenderer = (record: IFacility, status: UtilityType) => {
    return (
      <>
        {!record.address || record.status === "out_of_scope" ? (
          "-"
        ) : (
          <Space>
            <span>{record[`${status}_not_used`] ? "Off" : "On"}</span>
            {record[`${status}_status`] === "complete" ? <CompleteIcon /> : <IncompleteIcon />}
          </Space>
        )}
      </>
    );
  };

  const utilityStatusColumn: Column<IFacility>[] = !showUnrelated
    ? [
        {
          label: "Electricity",
          key: "electricity_not_used",
          minWidth: "105px",
          maxWidth: "105px",
          renderer: (record) => notUsedRenderer(record, "electricity"),
        },
        {
          label: "Heat",
          key: "heat_not_used",
          minWidth: "70px",
          maxWidth: "70px",
          renderer: (record) => notUsedRenderer(record, "heat"),
        },
        {
          label: "Water",
          key: "water_not_used",
          minWidth: "80px",
          maxWidth: "80px",
          renderer: (record) => notUsedRenderer(record, "water"),
        },
        {
          label: "Cooling",
          key: "cooling_not_used",
          minWidth: "90px",
          maxWidth: "90px",
          renderer: (record) => notUsedRenderer(record, "cooling"),
        },
        {
          label: "Consumables",
          key: "consumable_not_used",
          minWidth: "120px",
          maxWidth: "120px",
          renderer: (record) => notUsedRenderer(record, "consumable"),
        },
        {
          label: "Residual waste",
          key: "residual_waste_not_used",
          minWidth: "140px",
          maxWidth: "140px",
          renderer: (record) => notUsedRenderer(record, "residual_waste"),
        },
        {
          label: "Goods & services",
          key: "goods_and_services_not_used",
          minWidth: "145px",
          maxWidth: "180px",
          renderer: (record) => notUsedRenderer(record, "goods_and_services"),
        },
        {
          label: "Logistics packaging",
          key: "packaging_not_used",
          minWidth: "165px",
          maxWidth: "170px",
          renderer: (record) => notUsedRenderer(record, "packaging"),
        },
      ]
    : [];

  const statusColumn = !showUnrelated
    ? [
        {
          label: "Status",
          key: "status",
          filterable: true,
          minWidth: "140px",
          renderer: (facility: IFacility) => (
            <Space>
              <StatusTag
                background="white"
                status={facility.status}
                showIcon={false}
              />
              {facility.published_id && (
                <Tooltip
                  title={
                    "This facility is published and public to anyone with whom you share the generated URL with. They can read and edit the data."
                  }
                >
                  <GlobalOutlined
                    style={{
                      color: "#8C8C8C",
                    }}
                  />
                </Tooltip>
              )}
            </Space>
          ),
        },
      ]
    : [];

  const relationColumn = showUnrelated
    ? [
        {
          label: "Relation",
          key: "has_relation",
          minWidth: "150px",
          maxWidth: "150px",
          renderer: (record: IFacility) =>
            !record.has_relation && (
              <Tooltip title="Not related to purchase or sales orders">
                <>
                  <UnrelatedTag />
                </>
              </Tooltip>
            ),
          filterable: true,
        },
      ]
    : [];

  const columns: Column<IFacility>[] = [
    {
      label: "Facility name",
      key: "facility_name",
      searchable: true,
      sortable: true,
      minWidth: "180px",
      maxWidth: "2fr",
      renderer: (record) => {
        return (
          <div className={styles["facility-name"]}>
            <span className={styles.name}>{record.facility_name}</span>
            <span className={styles.address}>{record.address}</span>
          </div>
        );
      },
    },
    {
      label: "Category",
      key: "category",
      minWidth: "170px",
      searchable: true,
      sortable: true,
    },
    ...utilityStatusColumn,
    ...relationColumn,
    {
      label: "Type",
      key: "type",
      minWidth: "170px",
      filterable: true,
      sortable: true,
    },
    ...statusColumn,
    {
      key: "actions",
      label: <></>,
      minWidth: "40px",
      maxWidth: "40px",
      noLink: true,
      align: "end",
      renderer: (facility) => (
        <ActionsPopover
          actions={
            <>
              <DeleteFacilityAction
                deselectUndeletable={deselectUndeletableFacilities}
                onSucces={(deletedIds) => {
                  if (deletedIds.includes(facilityId)) {
                    navigate("/facilities");
                  }
                  setSelectedFacilities([]);
                  analytics.track("Material Deleted");
                }}
                facilities={[facility]}
              >
                <ActionsPopOverItem
                  disabled={!facility.deletable}
                  danger
                  icon={<DeleteOutlined />}
                >
                  Delete facility
                </ActionsPopOverItem>
              </DeleteFacilityAction>
            </>
          }
        >
          <MoreOutlined />
        </ActionsPopover>
      ),
    },
  ];

  const { pagination, filter, search, sort, onSearchFilterSortPaginate, setPage } =
    useTableStateUrl({
      columns,
    });

  const resetTable = () => {
    setPage(1);
    setSelectedFacilities([]);
  };

  const { data, isLoading, isFetching } = useGetFacilities({
    filter,
    sort,
    search,
    pagination,
    enabled: !!selectedYear,
    year: selectedYear,
    showUnrelated: showUnrelated,
  });
  const { data: optionData, isLoading: optionIsLoading } = useGetFacilityOverviewYears();

  return (
    <div>
      <PageHeader
        title={translation.facility.pageTitle}
        description={translation.facility.pageDescription}
        right={
          <>
            <HelpButton
              url="https://help.root-sustainability.com/en/articles/9898817-facilities"
              type={"link"}
            />
            <PageButtonGroup loading={isLoading}>
              <>
                <ActionsPopover
                  actions={
                    <>
                      <UploadFacilitiesButton />
                      <UploadUtilitiesButton year={selectedYear} />
                      <UploadGoodsAndServices year={selectedYear} />
                      <UploadLogisticsPackaging year={selectedYear} />
                    </>
                  }
                >
                  <Button icon={<UploadOutlined />}>Upload</Button>
                </ActionsPopover>
                <ActionsPopover
                  actions={
                    <>
                      <FileDownloader
                        fileUrlFn={getExportFacilitiesUrl}
                        fileOutputName="export-facilities.xlsx"
                        addTimestampAndCompany={true}
                        customRender={(props) => (
                          <ActionsPopOverItem
                            {...props}
                            black
                            icon={<FacilitiesIcon />}
                          >
                            Facilities
                          </ActionsPopOverItem>
                        )}
                      />
                      {selectedYear && (
                        <FileDownloader
                          fileUrlFn={() => getExportUtilitiesUrl(selectedYear)}
                          fileOutputName={`export-utilities-${selectedYear}.xlsx`}
                          addTimestampAndCompany={true}
                          customRender={(props) => (
                            <ActionsPopOverItem
                              {...props}
                              black
                              icon={<ThunderboltOutlined />}
                            >
                              Utilities
                            </ActionsPopOverItem>
                          )}
                        />
                      )}
                      {selectedYear && (
                        <FileDownloader
                          fileUrlFn={() => getExportGoodsAndServicesUrl(selectedYear)}
                          fileOutputName={`export-goods-and-services-${selectedYear}.xlsx`}
                          addTimestampAndCompany={true}
                          customRender={(props) => (
                            <ActionsPopOverItem
                              {...props}
                              black
                              icon={<CapitalGoodsIcon />}
                            >
                              Goods & services
                            </ActionsPopOverItem>
                          )}
                        />
                      )}
                      {selectedYear && (
                        <FileDownloader
                          fileUrlFn={() => getExportLogisticsPackagingUrl(selectedYear)}
                          fileOutputName={`export-logistics-packaging-${selectedYear}.xlsx`}
                          addTimestampAndCompany={true}
                          customRender={(props) => (
                            <ActionsPopOverItem
                              {...props}
                              black
                              icon={
                                <PackageIcon
                                  width={14}
                                  height={14}
                                />
                              }
                            >
                              Logistics packaging
                            </ActionsPopOverItem>
                          )}
                        />
                      )}
                    </>
                  }
                >
                  <Button icon={<DownloadOutlined />}>Export</Button>
                </ActionsPopover>
                <ActionsPopover
                  actions={
                    <>
                      <DownloadFacilitiesTemplate
                        customRender={(props) => (
                          <ActionsPopOverItem
                            {...props}
                            black
                            icon={<FacilitiesIcon />}
                          >
                            Facilities
                          </ActionsPopOverItem>
                        )}
                      />
                      <DownloadUtilitiesTemplate
                        customRender={(props) => (
                          <ActionsPopOverItem
                            {...props}
                            black
                            icon={<ThunderboltOutlined />}
                          >
                            Utilities
                          </ActionsPopOverItem>
                        )}
                      />
                      <DownloadGoodsAndServicesTemplate
                        customRender={(props) => (
                          <ActionsPopOverItem
                            {...props}
                            black
                            icon={<CapitalGoodsIcon />}
                          >
                            Goods & services
                          </ActionsPopOverItem>
                        )}
                      />
                      <DownloadLogisticsPackagingTemplate
                        customRender={(props) => (
                          <ActionsPopOverItem
                            {...props}
                            black
                            icon={
                              <PackageIcon
                                width={14}
                                height={14}
                              />
                            }
                          >
                            Logistics packaging
                          </ActionsPopOverItem>
                        )}
                      />
                    </>
                  }
                >
                  <Button icon={<DownloadOutlined />}>Template</Button>
                </ActionsPopover>
                <Button onClick={() => navigate("/facilities/add")}>
                  <PlusOutlined /> Add
                </Button>
                <Dropdown
                  trigger={["click"]}
                  onOpenChange={handleOpenChange}
                  open={moreDropdownOpen}
                  menu={{
                    items: [
                      {
                        label: (
                          <DeleteFacilityAction
                            deselectUndeletable={deselectUndeletableFacilities}
                            onSucces={(deletedIds) => {
                              if (deletedIds.includes(facilityId)) {
                                navigate("/facilities");
                              }
                              setMoreDropdownOpen(false);
                            }}
                            facilities={[]}
                            deleteAll={true}
                          >
                            <Space>
                              <DeleteOutlined />
                              <span>Delete all unrelated facilities</span>
                            </Space>
                          </DeleteFacilityAction>
                        ),
                        key: "1",
                        danger: true,
                      },
                    ],
                  }}
                >
                  <Button icon={<MoreOutlined />} />
                </Dropdown>
              </>
            </PageButtonGroup>
          </>
        }
      />
      <TableHeader
        left={
          <>
            {selectedFacilities.length > 0 ? (
              <span className={styles["num-selected"]}>{selectedFacilities.length} selected:</span>
            ) : (
              <YearSelector
                optionData={optionData}
                isLoading={optionIsLoading}
                disableYears={showUnrelated}
                onChange={resetTable}
                right={
                  <Tooltip
                    placement="bottom"
                    title="Show facilities that are not related to purchase or sales orders and don't have contracts"
                  >
                    <>
                      <SwitchWithLabel
                        togglePosition="right"
                        size="small"
                        checked={showUnrelated}
                        onChange={(disabled) => {
                          resetTable();
                          setShowUnrelated(disabled);
                        }}
                      >
                        Year unrelated
                      </SwitchWithLabel>
                    </>
                  </Tooltip>
                }
              />
            )}
            {selectedFacilities.length > 0 && (
              <>
                <span className={styles["num-selected"]}>
                  {selectedFacilities.length} selected:
                </span>
                <FakeLink onClick={() => setSelectedFacilities([])}>Clear selection</FakeLink>
                <Space.Compact>
                  <BulkEditScopeModal facilities={selectedFacilities}>
                    <Button
                      type={"primary"}
                      icon={<EditOutlined />}
                    >
                      Edit Scope
                    </Button>
                  </BulkEditScopeModal>
                  <DeleteFacilityAction
                    deselectUndeletable={deselectUndeletableFacilities}
                    onSucces={(deletedIds) => {
                      if (deletedIds.includes(facilityId)) {
                        navigate("/facilities");
                      }
                      setSelectedFacilities([]);
                    }}
                    facilities={selectedFacilities}
                  >
                    <Button
                      disabled={selectedFacilities.some((x) => !x.deletable)}
                      icon={<DeleteOutlined style={{ color: "#f5222d" }} />}
                    />
                  </DeleteFacilityAction>
                </Space.Compact>
              </>
            )}
          </>
        }
        right={<StatusTags data={data} />}
      />
      <div className={styles.UtilitiesOverview}>
        <div style={{ minWidth: "500px", width: "100%" }}>
          <Table<IFacility>
            checkable={true}
            data={data?.data}
            loading={isLoading || isFetching}
            columns={columns}
            selectedKeys={selectedFacilities.map((x) => x.id)}
            onSearchFilterSortPaginate={onSearchFilterSortPaginate}
            pagination={pagination}
            sort={sort}
            filter={filter}
            onSelectedKeysChange={onSelectedKeysChange}
            search={search}
            activeRowKey={facilityId}
            totalRows={data?.total_count}
            availableFilters={{
              status: [
                { label: "Complete", value: "complete" },
                { label: "Incomplete", value: "incomplete" },
                { label: "Out of scope", value: "out_of_scope" },
              ],
              has_relation: [
                { label: "Related", value: "1" },
                { label: "Unrelated", value: "0" },
              ],
              type: facilityTypeLabels.map((label) => ({
                label: label,
                value: label,
              })),
            }}
            rowKey={"id"}
            rowLink={(record) => `/facilities/${record.id}`}
          />
        </div>
      </div>
    </div>
  );
}
